import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { Cafe, Dish, DishUnit, LegalEntity, Order, OrderItem, PromotionApplied } from '../../apiTypes';
import { t } from '../../constants/translations';
import { useLocale } from '../../contexts/locale.context';
import currency from '../../utils/currency';
import getBillCalc from '../../utils/getBillCalc';
import { Map } from '../../utils/map';

interface Props {
  items: Record<string, OrderItem>;
  number: number;
  created: number;
  service: number;
  discount: number;
  delivery?: Order['delivery'];
  cafe: DbDocument<Cafe> | null | undefined;
  dishesMap: Map<DbDocument<Dish>>;
  comment?: string;
  waiter?: string;
  promotionsApplied: PromotionApplied[];
  legalEntities: DbDocument<LegalEntity>[];
  legalEntitiesMap: Map<DbDocument<LegalEntity>>;
}

type BillItem = {
  dishId: string;
  name: string;
  price: number;
  qnt: number;
  weight: number;
  defaultWeight: number;
  unit?: DishUnit;
  modNames?: string[];
};

export const getBillItemTotal = (billItem: BillItem) =>
  billItem.weight ? (billItem.price / billItem.defaultWeight) * billItem.weight : billItem.price * billItem.qnt;

export const getBillItemPrice = (orderItem: OrderItem, dishesMap: Map<DbDocument<Dish>>): number => {
  const basePrice = Number(orderItem.price || dishesMap[orderItem.dishId]?.price || 0);
  if (orderItem.modifications && orderItem.modifications.length) {
    return orderItem.modifications.reduce((acc, mod) => acc + (mod.price || 0), basePrice);
  }
  return basePrice;
};

const Bill = ({
  items,
  number,
  created,
  service = 0,
  discount = 0,
  delivery,
  comment,
  waiter,
  cafe,
  dishesMap,
  promotionsApplied,
  legalEntities = [],
  legalEntitiesMap = {},
}: Props) => {
  const { lang } = useLocale();
  const fontWeight = cafe?.bill?.boldText ? 800 : 400;

  const billItemsMap = useMemo(
    () =>
      Object.values(items).reduce<Record<string, BillItem>>((acc, orderItem) => {
        const modNames = (orderItem.modifications || []).filter((m) => m.price).map((m) => m.name);
        const key = orderItem.dishId + modNames.join('');
        return {
          ...acc,
          [key]: {
            dishId: orderItem.dishId,
            name: orderItem.name || dishesMap[key]?.name || '',
            modNames,
            price: getBillItemPrice(orderItem, dishesMap),
            qnt: (acc[key]?.qnt || 0) + 1,
            weight: orderItem.byWeight ? (acc[key]?.weight || 0) + Number(orderItem.weight) : 0,
            defaultWeight: Number(orderItem.defaultWeight),
            unit: orderItem.unit,
          },
        };
      }, {}),
    [dishesMap, items]
  );

  const billCalc = getBillCalc({
    orderItems: Object.values(items),
    legalEntities,
    service,
    discount,
    promotionsApplied,
  });

  return (
    <Box p={2} pb={5} color="#000000">
      <div data-testid="bill-header">
        {cafe?.bill?.logo && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              sx={{
                width: 300,
                height: 170,
                objectFit: 'contain',
              }}
              src={cafe?.bill.logo}
            />
          </Box>
        )}

        {cafe?.bill?.title && (
          <Typography variant="h4" sx={{ mt: 2, textAlign: 'center', fontWeight }}>
            {cafe?.bill.title}
          </Typography>
        )}

        {cafe?.bill?.beforeText && (
          <Typography variant="h5" sx={{ mt: 1, textAlign: 'center', fontWeight }}>
            {cafe?.bill.beforeText}
          </Typography>
        )}
        {cafe?.bill?.beforeText2 && (
          <Typography sx={{ textAlign: 'center', fontWeight }}>{cafe?.bill.beforeText2}</Typography>
        )}

        <Typography
          variant="h6"
          sx={{ mt: 1, textAlign: 'center', fontWeight }}
        >{`${t.order[lang]} #${number}`}</Typography>

        <Typography sx={{ fontWeight }}>{`${t.date[lang]}: ${format(created, 'dd.MM.yyyy')}`}</Typography>

        {waiter && <Typography sx={{ fontWeight }}>{`${t.waiter[lang]}: ${waiter}`}</Typography>}

        {delivery && (
          <>
            <Typography sx={{ fontWeight }}>{`${t.customer[lang]}: ${delivery.name}`}</Typography>
            <Typography sx={{ fontWeight }}>{`${t.phone[lang]}: ${delivery.phoneNumber}`}</Typography>
            <Typography sx={{ fontWeight }}>{`${t.address[lang]}: ${delivery.address}`}</Typography>
            {delivery.comment && <Typography sx={{ fontWeight }}>{delivery.comment}</Typography>}
          </>
        )}

        {comment && <Typography sx={{ fontWeight }}>{comment}</Typography>}
      </div>

      <Grid container spacing={0.5} sx={{ borderBottom: '1px solid #555', mb: 1, mt: 2 }}>
        <Grid item xs={5.5}>
          <Typography variant="body2" sx={{ fontWeight }}>
            {t.name[lang]}
          </Typography>
        </Grid>
        <Grid item xs={1.5} textAlign="right">
          <Typography variant="body2" sx={{ fontWeight, whiteSpace: 'nowrap' }}>
            {t.qty[lang]}
          </Typography>
        </Grid>
        <Grid item xs={2.5} textAlign="right">
          <Typography variant="body2" sx={{ fontWeight }}>
            {t.price[lang]}
          </Typography>
        </Grid>
        <Grid item xs={2.5} textAlign="right">
          <Typography variant="body2" sx={{ fontWeight }}>
            {t.total[lang]}
          </Typography>
        </Grid>
      </Grid>

      {Object.values(billItemsMap).map((i, index) => {
        if (!i.name) {
          return null;
        }

        return (
          <Grid data-testid="bill-item" key={i.dishId + index} container spacing={0.5} sx={{ lineHeight: 1.1, mb: 1 }}>
            <Grid item xs={5} data-testid="bill-item-name">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>{i.name}</Typography>
              {!!i.modNames?.length && (
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.2, fontWeight }}>
                  {i.modNames.join(', ')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={2} textAlign="right" data-testid="bill-item-qnt">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>
                {i.weight ? ` ${i.weight}${t[i.unit || 'g'][lang]}` : i.qnt}
              </Typography>
            </Grid>
            <Grid item xs={2.5} textAlign="right" data-testid="bill-item-price">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>{currency(i.price)}</Typography>
            </Grid>
            <Grid item xs={2.5} textAlign="right" data-testid="bill-item-total">
              <Typography sx={{ lineHeight: 1.2, fontWeight }}>{currency(getBillItemTotal(i))}</Typography>
            </Grid>
          </Grid>
        );
      })}

      <Grid data-testid="bill-total" container spacing={0.5} sx={{ borderTop: '1px solid #555', mt: 1 }}>
        <Grid item xs={8}>
          <Typography variant="h6" sx={{ fontWeight }}>
            {t.total[lang]}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Typography variant="h6" sx={{ fontWeight }}>
            {currency(billCalc.total.itemsTotalSum)}
          </Typography>
        </Grid>

        {!!billCalc.total.promotionsSum && (
          <>
            <Grid item xs={8}>
              <Typography variant="body2" sx={{ fontWeight }}>
                {t.promotions[lang]}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body2" sx={{ fontWeight }}>
                -{currency(billCalc.total.promotionsSum)}
              </Typography>
            </Grid>
          </>
        )}

        {!!billCalc.total.discountSum && (
          <>
            <Grid item xs={8}>
              <Typography variant="body2" sx={{ fontWeight }}>{`${t.discount[lang]} ${discount}%`}</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body2" sx={{ fontWeight }}>
                -{currency(billCalc.total.discountSum)}
              </Typography>
            </Grid>
          </>
        )}

        {!!billCalc.total.serviceSum && (
          <>
            <Grid item xs={8}>
              <Typography variant="body2" sx={{ fontWeight }}>{`${t.service_charge[lang]} ${service}%`}</Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body2" sx={{ fontWeight }}>
                {currency(billCalc.total.serviceSum)}
              </Typography>
            </Grid>
          </>
        )}

        {(!!service || !!discount || !!billCalc.total.promotionsSum) && (
          <Grid container spacing={0.5} sx={{ borderTop: '1px solid #555', mt: 1 }}>
            <Grid item xs={8}>
              <Typography variant="h5" sx={{ fontWeight }}>
                {t.to_pay[lang]}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="h5" sx={{ fontWeight }}>
                {currency(billCalc.total.toPay)}
              </Typography>
            </Grid>
          </Grid>
        )}

        {legalEntities.length > 0 &&
          Object.entries(billCalc.byLegalEntity).map(([legalEntityId, bill], index) => (
            <Grid
              key={legalEntityId}
              container
              spacing={0.5}
              sx={{ borderTop: index === 0 ? '1px solid #555' : undefined, mt: index === 0 ? 1 : 0 }}
            >
              <Grid item xs={8}>
                <Typography variant="body2" sx={{ fontWeight }}>
                  {legalEntitiesMap[legalEntityId]?.name || legalEntityId}
                </Typography>
              </Grid>
              <Grid item xs={4} textAlign="right">
                <Typography variant="body2" sx={{ fontWeight }}>
                  {currency(bill.toPay)}
                </Typography>
              </Grid>
            </Grid>
          ))}
      </Grid>

      <div data-testid="bill-footer">
        {cafe?.bill?.afterText && (
          <Typography sx={{ mt: 3, textAlign: 'center', fontWeight }}>{cafe?.bill.afterText}</Typography>
        )}
        {cafe?.bill?.afterText2 && (
          <Typography sx={{ textAlign: 'center', fontWeight }}>{cafe?.bill.afterText2}</Typography>
        )}
      </div>
    </Box>
  );
};

export default Bill;
